// =====================================================
// BED — Accès au brouillon serveur du wizard (migration 00231)
// =====================================================
// Toutes les écritures du participant passent par des RPC SECURITY DEFINER
// scopées par `bed_access_token` : la table `bed_drafts` n'a AUCUNE policy anon
// (cf. en-tête de 00231 — `form_submissions` est lisible par toute clé anon, on
// n'y met donc pas de passeports). Aucune de ces fonctions ne throw : elles
// renvoient un statut discriminé, comme le reste du module BED.

import { publicSupabase } from '../supabase';
import type { BedPrefill, BedPrefillSolde } from './prefill';
import type { BedDeferralMap, BedMissingItem, BedStepId, BedStepState } from './wizard-model';

// -----------------------------------------------------------------------
// TYPES
// -----------------------------------------------------------------------

/** Ligne de la grille tarifaire exposée au participant. */
export interface BedPricingItem {
  pricing_item_id: string;
  label: string;
  amount_cents: number;
  /** Document de conditions (assureur). Non nul → « j'ai pris connaissance » exigé. */
  conditions_url: string | null;
  /**
   * 00273 — code du poste dans la grille (saisi par le staff, facultatif). Seul
   * critère qui distingue le poste d'engagement des souscriptions obligatoires.
   * Absent d'un serveur pas encore migré.
   */
  code?: string | null;
}

/** Signature tracée d'un participant. */
export interface BedSignature {
  /** PNG en data-URL (cf. justification §6 de 00231). */
  data_url: string;
  signed_at: string;
}

/** Certificat médical déposé (bucket privé `bed-uploads`). */
export interface BedCertificate {
  path: string;
  name: string;
  size: number;
  uploaded_at: string;
}

/** Sélection d'options + preuves de lecture, telles que persistées. */
export interface BedDraftOptions {
  selected_item_ids: string[];
  /** `{ [pricing_item_id]: conditions_url affichée au moment de la coche }`. */
  acknowledged: Record<string, string>;
  /** Chambre double Marrakech (question obligatoire étape ⑤). */
  double_room?: 'oui' | 'non';
}

export interface BedDraft {
  values: Record<string, string>;
  deferred: BedDeferralMap;
  step_states: Partial<Record<BedStepId, BedStepState>>;
  last_step: number | null;
  signatures: Partial<Record<'pilot' | 'copilot', BedSignature>>;
  options: Partial<BedDraftOptions>;
  medical_certificates: Partial<Record<'pilot' | 'copilot', BedCertificate>>;
  amount_to_pay_cents: number | null;
  missing_items: BedMissingItem[];
  submitted_at: string | null;
  /** 00248 : dernière re-soumission par le participant, NULL si jamais rééditée. */
  resubmitted_at: string | null;
  payment_case_id: string | null;
  updated_at: string;
}

export interface BedBootstrap {
  prefill: BedPrefill;
  solde: BedPrefillSolde | null;
  /** Date proposée pour « je m'engage à communiquer avant le … ». */
  info_deadline: string | null;
  mandatory_items: BedPricingItem[];
  optional_items: BedPricingItem[];
  draft: BedDraft | null;
  /**
   * Statut CRM du dossier APRÈS l'ouverture du lien (migration 00232) :
   * `get_bed_bootstrap` pose « BED téléchargé » si le porteur était encore
   * pré-inscrit, puis renvoie l'état d'après. Relayé tel quel dans l'événement
   * postMessage `loaded`.
   */
  bed_status: string | null;
  bed_status_label: string | null;
  /**
   * Reprise du paiement (migration 00237) : renseigné dès que le bulletin est
   * SOUMIS. `available` dit s'il reste quelque chose à régler — c'est ce qui
   * décide de l'affichage du CTA « Reprendre le paiement » au rechargement.
   *
   * Volontairement SANS `bed_token` : le bootstrap est appelé à chaque
   * chargement, il n'émet aucun jeton de tunnel. Le jeton est obtenu au clic,
   * par `resumeBedPayment`.
   */
  resume_payment: BedResumePaymentState | null;
  /**
   * Réédition d'un bulletin déjà soumis (migration 00248, retour recette du
   * 04/09/2026) : vrai tant que l'équipage n'est ni « Inscrit » ni « BED
   * complet ». Décision SERVEUR (`bed_resubmit_allowed`) ; le wizard ne fait
   * qu'afficher « Compléter mon bulletin » quand c'est vrai.
   */
  resubmit_allowed: boolean;
  /**
   * Codes des éléments manquants déjà levés à la main par le staff
   * (`bed_missing_item_overrides`). À la réouverture, ces reports restent
   * cochés : on ne redemande pas au participant ce que le CRM a déjà reçu.
   */
  resolved_item_codes: string[];
  /**
   * Migration 00249 — un règlement a déjà été encaissé sur le dossier : le
   * montant choisi (`draft.amount_to_pay_cents`) est FIGÉ. Le tunnel en
   * soustrait le déjà-encaissé (00240) ; le réécrire relancerait la promesse à
   * zéro. En réédition, le champ « montant à régler maintenant » passe donc en
   * lecture seule.
   *
   * OPTIONNEL : pendant la fenêtre de déploiement, un serveur encore en 00248
   * ne renvoie pas la clé. Absente = « on ne sait pas » → aucun verrou annoncé.
   */
  amount_locked?: boolean;
  /**
   * Migration 00249 — le dossier est engagé (méthode de paiement posée,
   * encaissement, ou statut hors `draft`/`pending_card`) : le socle 00166
   * refuse toute nouvelle sélection d'options. On le DIT au participant au lieu
   * de lui laisser cocher des cases qui ne seront jamais facturées.
   *
   * OPTIONNEL : voir `amount_locked` (fenêtre de déploiement).
   */
  options_locked?: boolean;
  /**
   * Migration 00280 — PREMIER envoi : le dossier de paiement du porteur existe
   * déjà et le participant l'a engagé (moyen de paiement choisi dans le tunnel,
   * ou dossier réglé). Liste des options déjà au dossier (engagées, facturées) ;
   * aucune autre ne peut être ajoutée en ligne. `null` = rien de figé.
   *
   * OPTIONNEL : absente sur un serveur antérieur à 00280 (même traitement que
   * `null`).
   */
  first_submission_locked_option_ids?: string[] | null;
  /**
   * 00296 (revue du 09/10) — le serveur finalisera-t-il le bulletin SANS
   * paiement (`bed_free_finalization_allowed`) ? Le solde seul ne suffit pas :
   * une preuve d'acompte portée par un membre reste sur le chemin d'avant.
   * Absent d'un serveur sans 00296 : traité comme `false`.
   */
  free_finalization_allowed?: boolean;
  /**
   * Migration 00249 — éléments manquants EFFECTIFS : le snapshot serveur moins
   * les pièces que le staff a levées à la main. C'est cette liste, et non
   * l'archive brute `draft.missing_items`, qui doit piloter l'écran « bulletin
   * enregistré » et la bannière : sinon le participant lit « il reste 1
   * élément » alors que le CRM a déjà tout reçu.
   *
   * `null` tant que le bulletin n'a pas été soumis (rien n'a pu être levé).
   * ABSENTE sur un serveur encore en 00248 : même traitement que `null`, on
   * retombe sur l'archive (`pickEffectiveMissingItems`).
   */
  effective_missing_items?: BedMissingItem[] | null;
}

/** État de reprise renvoyé par le bootstrap (lecture seule, sans jeton). */
export interface BedResumePaymentState {
  available: boolean;
  settled: boolean;
  payment_case_id?: string;
  order_id?: string;
  amount_cents?: number;
  paid_cents?: number;
  remaining_cents?: number;
  /**
   * Montant CHOISI par le participant (migration 00240), borné au restant dû.
   *
   * `null` = aucun choix explicite → le tunnel encaissera le restant dû. C'est
   * ce montant, et pas le solde entier, que l'écran de reprise doit annoncer :
   * sinon le CRM promet 3 920 € là où le tunnel prélève 2 000 €.
   */
  amount_to_pay_cents?: number | null;
  /**
   * 00304 (TICKET-BED-31) : une remise posée après l'émission du dossier couvre
   * le solde. Aucune reprise n'est proposée (`available` false) et le dossier
   * n'est pas soldé (`settled` false) : le staff le finalise.
   */
  nothing_due?: boolean;
  /**
   * 00305 (TICKET-BED-32) : bulletin envoyé sans paiement, puis un solde est de
   * nouveau dû (réduction retirée ou grille modifiée). Aucun dossier de paiement :
   * pas de reprise possible, le service administratif envoie un lien.
   */
  debt_reopened?: boolean;
  /**
   * 00312 (TICKET-DEROG-04, blocage provisoire) : une dérogation d'âge manque
   * (29 ans ou plus au départ, case « Dérogation validée » non cochée). Aucune
   * reprise : le paiement du solde attend la validation de l'équipe.
   */
  derogation_pending?: boolean;
}

/** Reprise effective : l'état + le jeton de TUNNEL utilisable. */
export interface BedResumePayment extends BedResumePaymentState {
  bed_token: string;
  expires_at: string;
}

export type BedResumeResult =
  | { status: 'ok'; data: BedResumePayment }
  | { status: 'unavailable' }
  | { status: 'already_settled' }
  | { status: 'nothing_due' }
  /** 00312 : dérogation d'âge à valider, aucun jeton de tunnel émis. */
  | { status: 'derogation_pending' }
  | { status: 'error' };

/**
 * Obtient un jeton de TUNNEL valide pour reprendre le paiement d'un bulletin
 * déjà soumis. Réutilise le jeton encore valide, en émet un neuf sinon — c'est
 * ce dernier cas qui ferme le cul-de-sac quand le lien de paiement d'origine a
 * expiré (review Victor, P0 « cul-de-sac avant paiement »).
 */
export async function resumeBedPayment(token: string): Promise<BedResumeResult> {
  try {
    const { data, error } = await publicSupabase.rpc('bed_resume_payment', {
      p_bed_access_token: token,
    });

    if (error) {
      if (error.message?.includes('bed_resume_already_settled'))
        return { status: 'already_settled' };
      if (error.message?.includes('bed_resume_nothing_due')) return { status: 'nothing_due' };
      if (error.message?.includes('bed_resume_derogation_pending'))
        return { status: 'derogation_pending' };
      if (error.message?.includes('bed_resume_unavailable')) return { status: 'unavailable' };
      console.error('[BED-DRAFT] bed_resume_payment:', error);
      return { status: 'error' };
    }

    return { status: 'ok', data: data as BedResumePayment };
  } catch (err) {
    console.error('[BED-DRAFT] Exception bed_resume_payment:', err);
    return { status: 'error' };
  }
}

/** Patch d'auto-save : uniquement ce que l'étape courante a modifié. */
export interface BedDraftPatch {
  values?: Record<string, string>;
  deferred?: BedDeferralMap;
  step_states?: Partial<Record<BedStepId, BedStepState>>;
  last_step?: number;
  signatures?: Partial<Record<'pilot' | 'copilot', BedSignature>>;
  options?: BedDraftOptions;
  medical_certificates?: Partial<Record<'pilot' | 'copilot', BedCertificate>>;
  amount_to_pay_cents?: number | null;
}

export type BedTokenError =
  | 'invalid'
  | 'expired'
  | 'already_submitted'
  /**
   * 00249 — la sélection d'options ne peut plus changer : le paiement du
   * dossier a commencé (`bed_options_locked_on_resubmit`). Le bulletin, lui,
   * reste modifiable : ce n'est PAS `already_submitted`.
   */
  | 'options_locked'
  /** 00296 : rien à payer, une option payante exige un dossier (`bed_options_require_payment`). */
  | 'options_require_payment'
  /**
   * 00280 — un paiement par carte est en cours sur le dossier : son montant ne
   * peut pas être ajusté avant la fin de ce paiement
   * (`bed_amount_change_card_in_progress`). À réessayer quelques minutes plus tard.
   */
  | 'payment_in_progress'
  /**
   * 00312 (TICKET-DEROG-04) — rien à payer, mais une dérogation d'âge manque :
   * la finalisation (= inscription définitive) attend la validation de l'équipe.
   * Le brouillon reste enregistré.
   */
  | 'derogation_pending'
  | 'error';

export type BedBootstrapResult =
  | { status: 'ok'; data: BedBootstrap }
  | { status: BedTokenError; code?: string };

export type BedSaveResult =
  | { status: 'ok'; savedAt: string; missingItems: BedMissingItem[] }
  | { status: BedTokenError; code?: string };

// -----------------------------------------------------------------------
// MAPPING D'ERREURS
// -----------------------------------------------------------------------

/**
 * Les RPC lèvent des erreurs NOMMÉES (`bed_access_token_expired`, …). On les
 * traduit en statut, sans jamais exposer le message brut à l'utilisateur.
 */
function classifyError(message: string | undefined): BedTokenError {
  if (!message) return 'error';
  if (message.includes('_expired')) return 'expired';
  if (message.includes('_invalid')) return 'invalid';
  if (message.includes('already_submitted')) return 'already_submitted';
  // 00248 : re-soumission refusée (dossier validé entre-temps) — même issue
  // pour le participant : le bulletin n'est plus modifiable.
  if (message.includes('resubmission_locked')) return 'already_submitted';
  // 00249 : `bed_options_locked_on_resubmit` — SEULE la sélection d'options est
  // figée (le paiement a commencé). Le reste du bulletin reste modifiable :
  // surtout ne pas retomber sur `already_submitted`, qui annoncerait à tort un
  // dossier clos. 00280 : `bed_options_locked_after_payment` (option ajoutée au
  // premier envoi sur un dossier engagé) passe par le même statut.
  if (message.includes('options_locked')) return 'options_locked';
  if (message.includes('options_require_payment')) return 'options_require_payment';
  if (message.includes('card_in_progress')) return 'payment_in_progress';
  if (message.includes('derogation_pending')) return 'derogation_pending';
  return 'error';
}

// -----------------------------------------------------------------------
// APPELS
// -----------------------------------------------------------------------

/**
 * Charge en UN aller-retour tout ce dont le wizard a besoin : pré-remplissage
 * CRM, brouillon serveur du même lien, grille tarifaire (obligatoires +
 * options avec leurs documents), solde SERVEUR et deadline « infos manquantes ».
 */
export async function getBedBootstrap(token: string): Promise<BedBootstrapResult> {
  try {
    const { data, error } = await publicSupabase.rpc('get_bed_bootstrap', { p_token: token });
    if (error) {
      const status = classifyError(error.message);
      if (status === 'error') console.error('[BED-DRAFT] get_bed_bootstrap:', error);
      return { status, code: error.message };
    }
    if (!data) return { status: 'invalid' };
    return { status: 'ok', data: data as BedBootstrap };
  } catch (err) {
    console.error('[BED-DRAFT] Exception get_bed_bootstrap:', err);
    return { status: 'error' };
  }
}

/**
 * Enregistre l'étape courante. Le serveur fusionne le patch avec le brouillon
 * existant et RECALCULE les éléments manquants : c'est sa liste qui fait foi.
 */
export async function saveBedDraft(token: string, patch: BedDraftPatch): Promise<BedSaveResult> {
  try {
    const { data, error } = await publicSupabase.rpc('save_bed_draft', {
      p_token: token,
      p_patch: patch,
    });
    if (error) {
      const status = classifyError(error.message);
      if (status === 'error') console.error('[BED-DRAFT] save_bed_draft:', error);
      return { status, code: error.message };
    }
    const result = data as { saved_at: string; missing_items: BedMissingItem[] };
    return {
      status: 'ok',
      savedAt: result?.saved_at ?? new Date().toISOString(),
      missingItems: result?.missing_items ?? [],
    };
  } catch (err) {
    console.error('[BED-DRAFT] Exception save_bed_draft:', err);
    return { status: 'error' };
  }
}

export type BedSchoolResult =
  | { status: 'ok'; id: string; name: string; city: string; created: boolean }
  | { status: 'missing_city' }
  | { status: BedTokenError; code?: string };

/**
 * Crée (ou retrouve) l'école saisie par le participant. Cahier L46 : nom ET
 * ville obligatoires — la RPC refuse explicitement une création sans ville.
 */
export async function createBedSchool(
  token: string,
  name: string,
  city: string
): Promise<BedSchoolResult> {
  try {
    const { data, error } = await publicSupabase.rpc('bed_create_school', {
      p_token: token,
      p_name: name,
      p_city: city,
    });
    if (error) {
      if (error.message?.includes('school_name_and_city_required')) {
        return { status: 'missing_city' };
      }
      const status = classifyError(error.message);
      if (status === 'error') console.error('[BED-DRAFT] bed_create_school:', error);
      return { status, code: error.message };
    }
    const result = data as { id: string; name: string; city: string; created: boolean };
    return { status: 'ok', ...result };
  } catch (err) {
    console.error('[BED-DRAFT] Exception bed_create_school:', err);
    return { status: 'error' };
  }
}

/** Détail du solde renvoyé par le socle 00166 après finalisation. */
export interface BedBreakdown {
  mandatory_items: Array<BedPricingItem & { acknowledged: boolean }>;
  acompte_cents: number;
  reduction_cents: number;
  reduction_label: string | null;
  solde_base_cents: number | null;
  options_catalog: Array<BedPricingItem & { acknowledged: boolean; selected: boolean }>;
  subscribed_options: Array<{ label: string; amount_cents: number }>;
  required_acks_missing: Array<{ pricing_item_id: string; label: string }>;
  total_cents: number;
  status: string;
  locked: boolean;
  pricing_unverified: boolean;
  detail_stale: boolean;
}

export interface BedFinalizeParams {
  token: string;
  /** `null` (00296) : rien à payer, finalisation sans dossier de paiement. */
  paymentCaseId: string | null;
  submissionIds: string[];
  optionItemIds: string[];
  /** `{ [pricing_item_id]: conditions_url AFFICHÉE }` — le socle exige l'écho. */
  conditionsUrls: Record<string, string>;
  amountToPayCents: number | null;
}

export type BedFinalizeResult =
  | {
      status: 'ok';
      breakdown: BedBreakdown | null;
      missingItems: BedMissingItem[];
      amountToPayCents: number | null;
      complete: boolean;
      /** Statut CRM après la transition de soumission (00232). */
      bedStatus: string | null;
      /** 00296 : finalisé sans dossier, rien à payer. */
      nothingDue: boolean;
      /**
       * 00312 (TICKET-DEROG-04) : bulletin enregistré, mais le paiement du solde
       * attend la validation d'une dérogation d'âge. Le wizard n'ouvre pas le
       * paiement.
       */
      derogationPending: boolean;
      /**
       * 00312 : faux quand rien n'est à payer et qu'une dérogation manque — le
       * bulletin n'est PAS envoyé (son envoi vaut inscription définitive).
       */
      finalized: boolean;
    }
  | { status: BedTokenError; code?: string };

/**
 * Clôture la soumission : branche la sélection d'options et les prises de
 * connaissance sur le socle 00166 (`set_bed_case_options` /
 * `acknowledge_bed_conditions`), fige le brouillon et y archive la liste
 * structurée des éléments manquants. Aucun calcul financier n'est refait ici.
 */
export async function finalizeBedSubmission(params: BedFinalizeParams): Promise<BedFinalizeResult> {
  try {
    const { data, error } = await publicSupabase.rpc('finalize_bed_submission', {
      p_token: params.token,
      p_payment_case_id: params.paymentCaseId,
      p_submission_ids: params.submissionIds,
      p_option_item_ids: params.optionItemIds,
      p_conditions_urls: params.conditionsUrls,
      p_amount_to_pay_cents: params.amountToPayCents,
    });
    if (error) {
      const status = classifyError(error.message);
      if (status === 'error') console.error('[BED-DRAFT] finalize_bed_submission:', error);
      return { status, code: error.message };
    }
    const result = data as {
      breakdown: BedBreakdown | null;
      missing_items: BedMissingItem[];
      amount_to_pay_cents: number | null;
      complete: boolean;
      bed_status: string | null;
      nothing_due?: boolean;
      derogation_pending?: boolean;
      finalized?: boolean;
    };
    return {
      status: 'ok',
      breakdown: result?.breakdown ?? null,
      missingItems: result?.missing_items ?? [],
      amountToPayCents: result?.amount_to_pay_cents ?? null,
      complete: Boolean(result?.complete),
      bedStatus: result?.bed_status ?? null,
      nothingDue: Boolean(result?.nothing_due),
      derogationPending: Boolean(result?.derogation_pending),
      finalized: result?.finalized !== false,
    };
  } catch (err) {
    console.error('[BED-DRAFT] Exception finalize_bed_submission:', err);
    return { status: 'error' };
  }
}

/** Bucket privé dédié aux pièces jointes du BED (00231). */
export const BED_UPLOAD_BUCKET = 'bed-uploads';

export type BedUploadResult =
  | { status: 'ok'; certificate: BedCertificate }
  | { status: 'too_large' }
  | { status: 'bad_type' }
  | { status: 'error' };

const CERTIFICATE_MAX_BYTES = 8 * 1024 * 1024;
const CERTIFICATE_MIME = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'];

/**
 * Dépose un certificat médical. JAMAIS bloquant (cahier L120) : un échec ne
 * doit pas empêcher la soumission, l'appelant se contente de l'afficher.
 */
export async function uploadBedCertificate(
  token: string,
  role: 'pilot' | 'copilot',
  file: File
): Promise<BedUploadResult> {
  if (file.size > CERTIFICATE_MAX_BYTES) return { status: 'too_large' };
  if (file.type && !CERTIFICATE_MIME.includes(file.type)) return { status: 'bad_type' };

  // Nom de fichier assaini : le chemin est confiné au dossier du token.
  const safeName = file.name.replace(/[^\w.-]+/g, '_').slice(-80);
  const path = `${token}/${role}-${Date.now()}-${safeName}`;

  try {
    const { error } = await publicSupabase.storage
      .from(BED_UPLOAD_BUCKET)
      // 00235 : `upsert: false`. Les chemins portent un `Date.now()`, il n'y a
      // donc jamais de conflit — `upsert: true` suggérait un écrasement qui
      // n'existe pas. Un certificat ne se rature pas, il se remplace (et il
      // n'y a volontairement aucune policy UPDATE sur le bucket).
      .upload(path, file, { upsert: false, contentType: file.type || undefined });

    if (error) {
      console.error('[BED-DRAFT] upload certificat:', error);
      return { status: 'error' };
    }

    return {
      status: 'ok',
      certificate: {
        path,
        name: file.name,
        size: file.size,
        uploaded_at: new Date().toISOString(),
      },
    };
  } catch (err) {
    console.error('[BED-DRAFT] Exception upload certificat:', err);
    return { status: 'error' };
  }
}

/**
 * Supprime du bucket le fichier d'un certificat médical retiré du bulletin.
 *
 * Passe par l'edge function `bed-certificate-purge` (service_role) : Supabase
 * interdit le DELETE SQL sur `storage.objects`, et depuis 00235 le participant
 * n'a aucun droit de suppression sur le bucket (données de santé réservées à
 * l'administration). La décision d'effacer, elle, est prise EN BASE
 * (`bed_certificate_purgeable`, 00239) : jeton valide, chemin confiné au
 * dossier du jeton, plus aucune référence dans le brouillon.
 *
 * BEST-EFFORT, jamais bloquant : l'appelant (BedWizard) ne déclenche la purge
 * qu'APRÈS avoir persisté le retrait de la référence — sinon 00239 répond
 * `still_referenced` et refuse. Un échec laisse un fichier orphelin dans un
 * bucket privé — pas une fuite, mais rien ne le re-tentera : le chemin sort
 * de la file au premier essai.
 */
export async function purgeBedCertificate(token: string, path: string): Promise<boolean> {
  try {
    const { data, error } = await publicSupabase.functions.invoke('bed-certificate-purge', {
      body: { token, path },
    });
    if (error) {
      console.warn('[BED-DRAFT] purge certificat:', error);
      return false;
    }
    return Boolean((data as { purged?: boolean } | null)?.purged);
  } catch (err) {
    console.warn('[BED-DRAFT] Exception purge certificat:', err);
    return false;
  }
}

// ---------------------------------------------------------------------------
// 00252 — règles de l'événement et véhicule connu (retours 10, 11, 17)
// ---------------------------------------------------------------------------

export interface BedEventRules {
  info_deadline: string | null;
  passport_min_expiry: string | null;
  /** 00272 : référence de la règle d'âge (dérogation), identique au BPI. */
  event_start_date?: string | null;
  event_end_date: string | null;
  /** Retour 21 : date butoir de paiement du solde (NULL = mention générique). */
  balance_deadline: string | null;
}

/** Dates de règle du produit du lien (date butoir figée, passeport, fin d'événement). */
export async function getBedEventRules(token: string): Promise<BedEventRules | null> {
  try {
    const { data, error } = await publicSupabase.rpc('bed_event_rules', { p_token: token });
    if (error) {
      console.error('[BED-DRAFT] bed_event_rules:', error);
      return null;
    }
    return (data as BedEventRules) ?? null;
  } catch (err) {
    console.error('[BED-DRAFT] Exception bed_event_rules:', err);
    return null;
  }
}

/** Véhicule déjà connu du CRM → date de 1ère mise en circulation (ISO), sinon null. */
export async function getBedVehicleHint(
  token: string,
  registration: string
): Promise<string | null> {
  try {
    const { data, error } = await publicSupabase.rpc('bed_vehicle_hint', {
      p_token: token,
      p_registration: registration,
    });
    if (error) {
      console.error('[BED-DRAFT] bed_vehicle_hint:', error);
      return null;
    }
    const result = data as { found: boolean; circulation_date?: string | null } | null;
    return result?.found && result.circulation_date ? result.circulation_date : null;
  } catch (err) {
    console.error('[BED-DRAFT] Exception bed_vehicle_hint:', err);
    return null;
  }
}
